import React from 'react';
import styled from '@emotion/styled';
import { X, Settings as SettingsIcon } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.jsx';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 10998;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'all' : 'none'};
  transition: opacity 0.3s ease;
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: ${props => props.$visible ? '0' : '-400px'};
  bottom: 0;
  width: 400px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  box-shadow: -2px 0 20px rgba(0, 0, 0, 0.5);
  z-index: 10999;
  display: flex;
  flex-direction: column;
  transition: right 0.3s ease;
  color: white;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
`;

const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;

  .icon {
    width: 24px;
    height: 24px;
    color: #4CAF50;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const SettingSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem;
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  margin-bottom: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingLabel = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 4px;
  color: white;
`;

const SettingDescription = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
`;

const ToggleSwitch = styled.button`
  position: relative;
  width: 52px;
  height: 28px;
  background: ${props => props.$active ? '#4CAF50' : 'rgba(255, 255, 255, 0.2)'};
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: background 0.3s ease;
  flex-shrink: 0;
  margin-left: 16px;

  &:hover {
    background: ${props => props.$active ? '#45a049' : 'rgba(255, 255, 255, 0.25)'};
  }

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$active ? '26px' : '2px'};
    width: 24px;
    height: 24px;
    background: white;
    border-radius: 50%;
    transition: left 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${props => props.$variant === 'beta' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(76, 175, 80, 0.3)'};
  border: 1px solid ${props => props.$variant === 'beta' ? 'rgba(33, 150, 243, 0.5)' : 'rgba(76, 175, 80, 0.5)'};
  color: ${props => props.$variant === 'beta' ? '#64B5F6' : '#81C784'};
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 8px;
`;

const TextValue = styled.div`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
  font-size: 0.85rem;
  color: #4CAF50;
  background: rgba(76, 175, 80, 0.1);
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid rgba(76, 175, 80, 0.3);
  flex-shrink: 0;
  margin-left: 16px;
`;

/**
 * SettingsDrawer - Chrome-style settings panel
 *
 * Provides a comprehensive, expandable settings interface for Kuutio features.
 * Slides in from the left with categorized settings sections.
 */
export default function SettingsDrawer({ visible = false, onClose, settings = [] }) {
  const { t } = useTranslation();
  // Group settings by section
  const groupedSettings = settings.reduce((acc, setting) => {
    const section = setting.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(setting);
    return acc;
  }, {});

  return (
    <>
      <Overlay $visible={visible} />
      <Drawer $visible={visible}>
        <DrawerHeader>
          <DrawerTitle>
            <SettingsIcon className="icon" />
            {t('drawer.settings')}
          </DrawerTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          {Object.entries(groupedSettings).map(([sectionName, sectionSettings]) => (
            <SettingSection key={sectionName}>
              <SectionTitle>{sectionName}</SectionTitle>
              {sectionSettings.map((setting, idx) => (
                <SettingItem key={idx}>
                  <SettingInfo>
                    <SettingLabel>
                      {setting.label}
                      {setting.badge && <Badge $variant={setting.badge}>{setting.badge}</Badge>}
                    </SettingLabel>
                    {setting.description && (
                      <SettingDescription>{setting.description}</SettingDescription>
                    )}
                  </SettingInfo>
                  {setting.type === 'toggle' && (
                    <ToggleSwitch
                      $active={setting.value}
                      onClick={setting.onChange}
                      aria-label={setting.label}
                    />
                  )}
                  {setting.type === 'text' && (
                    <TextValue>{setting.value}</TextValue>
                  )}
                </SettingItem>
              ))}
            </SettingSection>
          ))}
        </DrawerContent>
      </Drawer>
    </>
  );
}
